import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import SignatureCanvas from "./SignatureCanvas";
import LogoHeader from "./LogoHeader";
import {
  fetchCartaPorte,
  viewPDFCartaPorte,
  firmarCartaPorte,
  eliminarCartaPorte,
  duplicarCartaPorte,
  actualizarPesoCartaPorte,
} from "../services/cartas_porte";
import { setCartasPorte } from "../redux/actions/app.actions";
import "./CartaPorteViewer.css";

function CartaPorteViewer() {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.auth?.currentUser?.data);
  const [cartaPorte, setCartaPorte] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showSignature, setShowSignature] = useState(false);
  const [showFirmaForm, setShowFirmaForm] = useState(false);
  const [firmanteNombre, setFirmanteNombre] = useState("");
  const [firmanteDni, setFirmanteDni] = useState("");
  const [emailManual, setEmailManual] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSigning, setIsSigning] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [pesoEditando, setPesoEditando] = useState(false);
  const [pesoValue, setPesoValue] = useState("");

  useEffect(() => {
    const cargarCartaPorte = async () => {
      try {
        setIsLoading(true);
        const cartaData = await fetchCartaPorte(id);
        setCartaPorte(cartaData);

        // Obtener URL del PDF
        try {
          const pdfBlob = await viewPDFCartaPorte(id);
          const pdfObjectUrl = URL.createObjectURL(pdfBlob);
          setPdfUrl(pdfObjectUrl);
        } catch (pdfErr) {
          // Si falla la carga del PDF, log del error pero continúa
          console.warn("No se pudo cargar PDF:", pdfErr);
          setError("PDF aún no disponible. Por favor, intenta más tarde o firma la carta para regenerarlo.");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Error al cargar la carta de porte");
        console.error("Error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      cargarCartaPorte();
    }
  }, [id]);

  const handleSignatureSubmit = async (firmaBase64) => {
    setIsSigning(true);
    setError(null);

    try {
      // Enviar firma al backend
      const emailDestinatario = cartaPorte?.destinatario_cliente?.email
        || cartaPorte?.destinatarioCliente?.email
        || emailManual || null;
      const response = await firmarCartaPorte(id, firmaBase64, firmanteNombre, firmanteDni, emailDestinatario);

      if (response) {
        // Actualizar estado local
        setCartaPorte({
          ...cartaPorte,
          estado: "approved",
          firma_destinatario: firmaBase64,
        });

        // Cerrar canvas de firma
        setShowSignature(false);
        setShowFirmaForm(false);

        // Mostrar confirmación
        alert("Carta de porte firmada correctamente");

        // Esperar a que el servidor termine de regenerar el PDF
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Recargar PDF regenerado (con cache-busting via timestamp en el service)
        const newPdfBlob = await viewPDFCartaPorte(id);
        const newPdfUrl = URL.createObjectURL(newPdfBlob);
        setPdfUrl(newPdfUrl);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error al firmar la carta de porte");
      console.error("Error:", err);
    } finally {
      setIsSigning(false);
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;

    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `CARTA-${cartaPorte?.ref}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async () => {
    if (!window.confirm("\u00bfSeguro que quieres eliminar esta carta de porte?")) return;
    setIsDeleting(true);
    try {
      await eliminarCartaPorte(id, user?.id);
      history.push("/ListScreen");
    } catch (err) {
      setError(err.response?.data?.message || "Error al eliminar la carta de porte");
    } finally {
      setIsDeleting(false);
    }
  };
  const handleDuplicar = async () => {
    if (!window.confirm("¿Crear una nueva carta de porte con los mismos datos?")) return;
    try {
      const nueva = await duplicarCartaPorte(id);
      console.log("Respuesta completa duplicación:", nueva);
      console.log("ID obtenido:", nueva?.id);
      
      if (nueva?.id) {
        alert("Carta de porte duplicada correctamente.");
        // Resetear el cache de Redux para forzar recarga en ListScreen
        dispatch(setCartasPorte([]));
        history.push("/ListScreen");
      } else {
        setError("No se obtuvo el ID de la nueva carta de porte. Por favor recarga la página.");
        console.error("Nueva carta sin ID:", nueva);
      }
    } catch (err) {
      console.error("Error al duplicar:", err);
      setError(`Error al duplicar la carta de porte: ${err.message || err}`);
    }
  };

  const handleGuardarPeso = async () => {
    if (!pesoValue || isNaN(pesoValue)) {
      alert("Introduce un peso válido en toneladas.");
      return;
    }
    try {
      const actualizada = await actualizarPesoCartaPorte(id, pesoValue);
      setCartaPorte((prev) => ({ ...prev, peso: actualizada?.peso ?? pesoValue }));
      setPesoEditando(false);
      setPesoValue("");
    } catch {
      setError("Error al guardar el peso.");
    }
  };
  if (isLoading) {
    return (
      <div className="carta-porte-viewer">
        <LogoHeader />
        <div className="viewer-wrapper">
          <div className="loading-spinner">
            <p>Cargando carta de porte...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!cartaPorte) {
    return (
      <div className="carta-porte-viewer">
        <LogoHeader />
        <div className="viewer-wrapper">
          <div className="error-alert">
            {error || "Carta de porte no encontrada"}
          </div>
          <button
            className="btn-back-list"
            onClick={() => history.push("/ListScreen")}
          >
            ← Volver a la lista
          </button>
        </div>
      </div>
    );
  }

  if (showFirmaForm && !showSignature) {
    const emailCliente = cartaPorte?.destinatario_cliente?.email || cartaPorte?.destino_obra?.email;
    const needsEmail = !emailCliente;
    const canProceed = firmanteNombre.trim() && firmanteDni.trim() && (!needsEmail || emailManual.trim());
    return (
      <div className="carta-porte-viewer">
        <LogoHeader />
        <div className="viewer-wrapper">
          <h3>Datos del firmante</h3>
          <div className="firma-form">
            <label>Nombre completo del firmante *</label>
            <input
              type="text"
              value={firmanteNombre}
              onChange={(e) => setFirmanteNombre(e.target.value)}
              placeholder="Nombre y apellidos"
              required
            />
            <label>DNI del firmante *</label>
            <input
              type="text"
              value={firmanteDni}
              onChange={(e) => setFirmanteDni(e.target.value)}
              placeholder="DNI / NIE"
              required
            />
            {emailCliente ? (
              <p className="firma-email-info">Se enviará confirmación a: <strong>{emailCliente}</strong></p>
            ) : (
              <>
                <label>Email del destinatario *</label>
                <input
                  type="email"
                  value={emailManual}
                  onChange={(e) => setEmailManual(e.target.value)}
                  placeholder="email@ejemplo.com"
                  required
                />
              </>
            )}
            <button
              className="btn-sign"
              disabled={!canProceed}
              onClick={() => setShowSignature(true)}
            >
              Continuar a firma
            </button>
            <button
              className="btn-cancel"
              onClick={() => setShowFirmaForm(false)}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showSignature) {
    return (
      <div className="carta-porte-viewer">
        <LogoHeader />
        <div className="viewer-wrapper">
          <SignatureCanvas
            onSign={handleSignatureSubmit}
            onCancel={() => setShowSignature(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="carta-porte-viewer">
      <LogoHeader />

      <div className="viewer-wrapper">
        <div className="viewer-header">
          <div className="header-content">
            <h2>Carta de Porte: {cartaPorte?.ref}</h2>
            <div className="status-badge" data-estado={cartaPorte?.estado}>
              {cartaPorte?.estado === "active"
                ? "Pendiente de Firma"
                : "Firmado"}
            </div>
          </div>
          <button
            className="btn-close-viewer"
            onClick={() => history.push("/ListScreen")}
            title="Cerrar"
          >
            ✕
          </button>
        </div>

        {error && <div className="error-alert">{error}</div>}

        <div className="viewer-content">
          <div className="viewer-info">
            <div className="info-section">
              <label>Tipo:</label>
              <span className="uppercase">
                {cartaPorte?.tipo === "maquina" ? "Carga" : "Árido"}
              </span>
            </div>

            <div className="info-section">
              <label>Fecha:</label>
              <span>
                {cartaPorte?.fecha
                  ? (() => {
                      const [y, m, d] = (cartaPorte.fecha.includes('-')
                        ? cartaPorte.fecha.split('-')
                        : cartaPorte.fecha.split('/').reverse());
                      return `${d}/${m}/${y}`;
                    })()
                  : "-"}{" "}
                {cartaPorte?.hora || ""}
              </span>
            </div>

            <div className="info-section">
              <label>Operador:</label>
              <span>{cartaPorte?.operador?.nombre || "-"}</span>
            </div>

            <div className="info-section">
              <label>Conductor:</label>
              <span>{cartaPorte?.conductor?.nombre || "-"}</span>
            </div>

            {/* CARGA - Detalles específicos */}
            {cartaPorte?.tipo === "maquina" && (
              <>
                <div className="info-section">
                  <label>Carga:</label>
                  <span>{cartaPorte?.maquina?.nombre || cartaPorte?.maquina_otro || "-"}</span>
                </div>

                <div className="info-section">
                  <label>Peso:</label>
                  <span>{cartaPorte?.maquina_peso ? cartaPorte?.maquina_peso + " Tn" : "-"}</span>
                </div>

                <div className="info-section">
                  <label>Origen:</label>
                  <span>{cartaPorte?.origen_obra?.nombre || cartaPorte?.origen_otro || "-"}</span>
                  {cartaPorte?.origen_obra?.direccion && (
                    <span className="info-sub">{cartaPorte.origen_obra.direccion}</span>
                  )}
                </div>

                <div className="info-section">
                  <label>Destino:</label>
                  <span>{cartaPorte?.destino_obra?.nombre || cartaPorte?.destino_otro || "-"}</span>
                  {cartaPorte?.destino_obra?.direccion && (
                    <span className="info-sub">{cartaPorte.destino_obra.direccion}</span>
                  )}
                </div>

                <div className="info-section">
                  <label>Destinatario:</label>
                  <span>{cartaPorte?.destinatario_obra_razon_social || cartaPorte?.destinatario_razon_social || "-"}</span>
                </div>
              </>
            )}

            {/* ÁRIDO - Detalles específicos */}
            {cartaPorte?.tipo === "arido" && (
              <>
                <div className="info-section">
                  <label>Material:</label>
                  <span>{cartaPorte?.material_arido?.nombre || cartaPorte?.tipo_material_otro || "-"}</span>
                </div>

                <div className="info-section">
                  <label>M³:</label>
                  <span>{cartaPorte?.m3 || "-"}</span>
                </div>

                <div className="info-section">
                  <label>Peso:</label>
                  <span>{cartaPorte?.peso ? cartaPorte?.peso + " Tn" : "-"}</span>
                </div>

                <div className="info-section">
                  <label>Origen (Planta):</label>
                  <span>{cartaPorte?.planta_arido?.nombre || cartaPorte?.origen_planta_otro || "-"}</span>
                </div>

                <div className="info-section">
                  <label>Destino (Obra):</label>
                  <span>{cartaPorte?.destino_obra?.nombre || cartaPorte?.destino_otro || "-"}</span>
                </div>

                <div className="info-section">
                  <label>Destinatario:</label>
                  <span>{cartaPorte?.destinatario_obra_razon_social || cartaPorte?.destinatario_razon_social || "-"}</span>
                </div>
              </>
            )}

            {cartaPorte?.estado === "active" ? (
              <div className="info-section">
                <label>Estado:</label>
                <span className="status-text active">
                  Esperando firma del cliente
                </span>
              </div>
            ) : (
              <div className="info-section">
                <label>Firmado en:</label>
                <span className="status-text approved">
                  {cartaPorte?.hora_firma_destino || "—"}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="viewer-actions">
          {cartaPorte?.estado === "active" && (
            <button
              className="btn-sign-carta"
              onClick={() => setShowFirmaForm(true)}
              disabled={isSigning}
            >
              {isSigning ? "Firmando..." : "Firmar Carta de Porte"}
            </button>
          )}

          <button
            className="btn-download"
            onClick={handleDownload}
            disabled={!pdfUrl}
          >
            Descargar PDF
          </button>

          {cartaPorte?.estado === "active" && (
            <button
              className="btn-delete-carta"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "Eliminando..." : "Eliminar carta"}
            </button>
          )}

          <button
            className="btn-sign-carta"
            onClick={handleDuplicar}
            style={{ background: "#2563eb" }}
          >
            Duplicar
          </button>

          {cartaPorte?.tipo === "arido" && !cartaPorte?.peso && (
            pesoEditando ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Peso (Tn)"
                  value={pesoValue}
                  onChange={(e) => setPesoValue(e.target.value)}
                  style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ccc", width: 120 }}
                />
                <button className="btn-sign-carta" onClick={handleGuardarPeso}>Guardar peso</button>
                <button className="btn-back-list" onClick={() => setPesoEditando(false)}>Cancelar</button>
              </div>
            ) : (
              <button
                className="btn-download"
                onClick={() => setPesoEditando(true)}
              >
                ⚖️ Añadir peso (Tn)
              </button>
            )
          )}

          <button
            className="btn-back-list"
            onClick={() => history.push("/ListScreen")}
          >
            Volver a la lista
          </button>
        </div>
      </div>
    </div>
  );
}

export default CartaPorteViewer;
